import { isPresent } from '@mbfab/shared'
import type { LucideIcon } from 'lucide-react'
import { File, FileImage, FileSpreadsheet, FileText } from 'lucide-react'
import type { MouseEvent } from 'react'

import { buttonVariants } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

// mbscan manager-client の同名部品と共通。片側だけで変えない
// 使い方のルール: docs/develop/client_ui/components/data-table.md

export type DataTableFileKind = 'pdf' | 'spreadsheet' | 'image' | 'file'

const fileIcons: Record<DataTableFileKind, { icon: LucideIcon; className: string }> = {
  pdf: { icon: FileText, className: 'text-red-600' },
  spreadsheet: { icon: FileSpreadsheet, className: 'text-emerald-700' },
  image: { icon: FileImage, className: 'text-sky-700' },
  file: { icon: File, className: 'text-muted-foreground' },
}

// アイコンボタンの見た目。「開く」「紐付け解除」等のメニューを持つファイル列（ミルシート一覧の PDF 等）は
// DataTableFileIcon ではなく Menu.Trigger にこのクラスと FileKindIcon を当て、見た目だけ揃える
export function dataTableFileIconClass(kind: DataTableFileKind = 'pdf') {
  return cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }), 'relative', fileIcons[kind].className)
}

// 複数ファイルのときは件数をアイコン右下に重ねる（ファイル名を並べて列を広げないため）
export function FileKindIcon({ kind = 'pdf', count }: { kind?: DataTableFileKind; count?: number }) {
  const Icon = fileIcons[kind].icon
  return (
    <>
      <Icon aria-hidden="true" />
      {count !== undefined && count > 1 && (
        <span className="absolute -right-1 -bottom-1 min-w-4 rounded-full bg-foreground px-1 text-[10px] leading-4 font-semibold text-background tabular-nums">
          {count}
        </span>
      )}
    </>
  )
}

// 一覧テーブルのファイル列の共通部品。ファイル名や「PDF」ボタンを列に並べると列幅を取り、
// 画面ごとに見た目もばらついていたため（2026-10-31）、枠線なしのファイルアイコン1つに揃える。
// ファイル名はホバー・フォーカスのツールチップと読み上げ用ラベルで伝える。
// ファイルが無い行は「—」にして、空欄と区別する（valueOrDash と同じ）
export function DataTableFileIcon({
  kind = 'pdf',
  fileName,
  href,
  onOpen,
  disabled = false,
  testId,
}: {
  kind?: DataTableFileKind
  // ツールチップ・aria-label に出す名前。ファイル名が無い場合は列名（「ミルシートPDF」等）を渡す
  fileName: string
  // URL を直接開ける場合は href（新しいタブで開く）、プレビューダイアログ・署名付きURL取得など
  // 処理を挟む場合は onOpen を渡す。どちらも無ければファイル無しとして「—」を出す
  href?: string | null
  onOpen?: () => void
  disabled?: boolean
  testId?: string
}) {
  const hasHref = isPresent(href)
  if (!hasHref && onOpen === undefined) {
    return <span className="text-muted-foreground">—</span>
  }

  const triggerClass = dataTableFileIconClass(kind)
  const label = `${fileName}を開く`
  const icon = <FileKindIcon kind={kind} />
  // 行クリックやリンク列の操作と干渉しないよう、アイコンの押下は行へ伝播させない
  const stopPropagation = (event: MouseEvent) => {
    event.stopPropagation()
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          hasHref && !disabled ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              aria-label={label}
              data-testid={testId}
              className={triggerClass}
              onClick={stopPropagation}
            />
          ) : (
            <button
              type="button"
              aria-label={label}
              data-testid={testId}
              disabled={disabled}
              className={triggerClass}
              onClick={(event) => {
                stopPropagation(event)
                onOpen?.()
              }}
            />
          )
        }
      >
        {icon}
      </TooltipTrigger>
      <TooltipContent>{fileName}</TooltipContent>
    </Tooltip>
  )
}
